import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SidebarItem from '../Sidebar/SidebarItem';
import SpeechBubblePortal from '../SpeechBubblePortal';

const STORAGE_KEY = 'faq-message-dismissed';

interface FAQSidebarItemProps {
  expandDueToChildren: boolean;
  onHoverIntent: (hasChildren: boolean) => void;
  disabled?: boolean;
}

const FAQSidebarItem: React.FC<FAQSidebarItemProps> = ({
  expandDueToChildren,
  onHoverIntent,
  disabled = false,
}) => {
  const location = useLocation();
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [refReady, setRefReady] = useState(false);
  const sidebarItemRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile) {
      hamburgerRef.current = document.querySelector('.sidebar-hamburger');
    }
  }, [isMobile]);

  const handleDismiss = () => {
    setShowSpeechBubble(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleFAQClick = () => {
    // Dismiss speech bubble when FAQ is clicked
    if (showSpeechBubble) {
      setShowSpeechBubble(false);
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  };

  // Mark ref as ready once it's mounted
  useLayoutEffect(() => {
    if (sidebarItemRef.current) {
      setRefReady(true);
    }
  }, []);

  // Show speech bubble on first visit if not dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);

    if (dismissed === 'true') {
      setShowSpeechBubble(false);
      return;
    }

    if (refReady) {
      const timer = setTimeout(() => {
        setShowSpeechBubble(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowSpeechBubble(false);
    }
  }, [refReady]);

  return (
    <>
      <Box sx={{ display: 'inline-block', width: '100%' }}>
        <SidebarItem
          ref={sidebarItemRef}
          to="/faq"
          label="FAQ"
          icon="Help"
          selected={location.pathname === '/faq'}
          expanded={expandDueToChildren}
          onHoverIntent={onHoverIntent}
          onClick={handleFAQClick}
          disabled={disabled}
        />
      </Box>

      <SpeechBubblePortal
        dismissable={true}
        tone="tone4"
        show={showSpeechBubble && !!(isMobile ? hamburgerRef.current : sidebarItemRef.current)}
        message="Have questions? Check out our FAQ section for answers to common questions!"
        targetRef={isMobile ? hamburgerRef : sidebarItemRef}
        onClose={handleDismiss}
      />
    </>
  );
};

export default FAQSidebarItem;
